import { useContext } from 'react';
import { getApolloContext } from '@apollo/client';
import { StationeryProductsDocument, useStationeryProductsQuery } from './useProducts.graphql.generated';
import type { StationeryProductsQuery } from './useProducts.graphql.generated';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import { withWindow } from '@shared/utils/withWindow';
import { addError } from '@shared/utils/logger';

const ZERO_PRODUCTS: StationeryProduct[] = []; // For stability

/**
 * Whether the products catalog is already in the Apollo cache — on the server because
 * `prefetchStationeryProducts` put it there for a crawlable `format:*` / `product:magnet` gallery
 * URL, on the client because `extract()` inlined that same cache as `window.__INITIAL_APOLLO_STATE__`
 * (PRNT-5582). The client's first render reads the server's own `extract()` back, which keeps the
 * two halves of `useIsPublicGallerySsr` in step.
 *
 * Not reactive, so a later client fetch can move it false → true without re-rendering. Left that
 * way deliberately: no consumer acts on a flip — `useDynamicBannerState` takes its banner as a
 * `useState` initialiser, `useValuePropsPayload` gates on `!isMounted`, and `useDesignTemplates`
 * only un-skips a `cache-only` summary that full data outranks. Freeze it app-wide (as
 * `ThemeSearchIndexProvider` does for the index) if one ever does.
 *
 * `stationeryProducts` takes no arguments, so the entry is keyed the same however it was written —
 * the `batchMode` variable every caller adds is not part of the cache key.
 */
export const useHasStationeryProducts = (): boolean => {
  // The context directly rather than `useApolloClient`, which throws without a provider: the gate
  // needs "no client" to answer false, its conservative default everywhere else.
  const { client } = useContext(getApolloContext());
  if (!client) return false;
  try {
    return !!client.readQuery<StationeryProductsQuery>({ query: StationeryProductsDocument })?.stationeryProducts?.length;
  } catch {
    // A partial cache makes `readQuery` throw rather than answer null; that is "not usable here".
    return false;
  }
};

/**
 * The product list plus whether it has settled.
 *
 * Callers that only render the list can use `useProducts` and ignore this — an empty array renders
 * as nothing either way. A caller that *acts* on the list needs the flag: an empty array is both
 * "not back yet" and "genuinely none", and treating the second as the first hangs forever.
 */
export const useProductsWithStatus = (options?: Readonly<{ fetchOnServer?: boolean }>): { products: StationeryProduct[]; isLoading: boolean } => {
  // Apollo skips an `ssr: false` query outright during SSR — it would not even read the seeded
  // cache — so this opts in where the catalog is already there, or where the caller asks the
  // server to fetch it (the public PDP's matching-designs rail, PRNT-5556).
  const hasPrefetchedProducts = useHasStationeryProducts();
  // A server fetch holds the HTML, so it gets one attempt, and a failure leaves the list empty
  // rather than failing the page: nothing is cached, so the client starts from the same empty list.
  const isServerFetch = withWindow(() => false, !!options?.fetchOnServer);
  const {
    data: productData,
    loading,
    error: productsError,
  } = useStationeryProductsQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: hasPrefetchedProducts || !!options?.fetchOnServer,
    context: isServerFetch ? { noRetry: true } : undefined,
  });

  if (productsError) {
    if (!isServerFetch) throw productsError;
    addError(productsError, { context: 'StationeryProductsServerFetchFailed' });
  }

  return { products: productData?.stationeryProducts ?? ZERO_PRODUCTS, isLoading: loading && !productData };
};

export const useProducts = (options?: Readonly<{ fetchOnServer?: boolean }>): StationeryProduct[] => useProductsWithStatus(options).products;
